// Cliente fetch para apps/projects/ (Django Ninja).
// Endpoints (registrados via /api/v1/projects):
//   GET  /api/v1/projects/
//   GET  /api/v1/projects/{id}/
//   POST /api/v1/projects/from-quote/{quote_id}/
//   POST /api/v1/projects/{id}/milestones/{mid}/complete/
//   POST /api/v1/projects/{id}/invoice-now/
//   GET  /api/v1/projects/{id}/time-entries/
//   POST /api/v1/projects/{id}/time-entries/
//   GET  /api/v1/projects/{id}/hours-summary/

import { ensureFreshToken } from '@/lib/tokenRefresh';

import type {
  CreateProjectFromQuoteIn,
  HoursSummaryOut,
  InvoiceRequestOut,
  MilestoneCompleteIn,
  MilestoneOut,
  ProjectOut,
  ProjectStatus,
  TimeEntryCreateIn,
  TimeEntryOut,
} from './types';

const DJANGO_PROXY = '/django-api';

async function authHeaders(): Promise<Record<string, string>> {
  await ensureFreshToken();
  const token = localStorage.getItem('auth_token');
  const orgId = localStorage.getItem('org_id') || '';
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  };
  if (token) headers.Authorization = `Bearer ${token}`;
  if (orgId) headers['X-Org-Id'] = orgId;
  return headers;
}

export class ProjectsApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'ProjectsApiError';
    this.status = status;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = await authHeaders();
  const res = await fetch(`${DJANGO_PROXY}${path}`, {
    ...init,
    headers: { ...headers, ...(init?.headers || {}) },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const detail = (body as { detail?: string }).detail || `HTTP ${res.status}`;
    throw new ProjectsApiError(
      typeof detail === 'string' ? detail : JSON.stringify(detail),
      res.status,
    );
  }
  if (res.status === 204) return undefined as T;
  return res.json();
}

export const projectsApi = {
  list: (status?: ProjectStatus): Promise<ProjectOut[]> => {
    const qs = status ? `?status=${encodeURIComponent(status)}` : '';
    return request<ProjectOut[]>(`/api/v1/projects/${qs}`);
  },

  get: (projectId: string): Promise<ProjectOut> =>
    request<ProjectOut>(`/api/v1/projects/${projectId}/`),

  createFromQuote: (
    quoteId: string,
    data: CreateProjectFromQuoteIn,
  ): Promise<ProjectOut> =>
    request<ProjectOut>(`/api/v1/projects/from-quote/${quoteId}/`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  completeMilestone: (
    projectId: string,
    milestoneId: string,
    data: MilestoneCompleteIn = {},
  ): Promise<MilestoneOut> =>
    request<MilestoneOut>(
      `/api/v1/projects/${projectId}/milestones/${milestoneId}/complete/`,
      { method: 'POST', body: JSON.stringify(data) },
    ),

  invoiceNow: (projectId: string): Promise<InvoiceRequestOut> =>
    request<InvoiceRequestOut>(`/api/v1/projects/${projectId}/invoice-now/`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),

  listTimeEntries: (
    projectId: string,
    billable?: boolean,
  ): Promise<TimeEntryOut[]> => {
    const qs =
      billable === undefined ? '' : `?billable=${billable ? 'true' : 'false'}`;
    return request<TimeEntryOut[]>(
      `/api/v1/projects/${projectId}/time-entries/${qs}`,
    );
  },

  createTimeEntry: (
    projectId: string,
    data: TimeEntryCreateIn,
  ): Promise<TimeEntryOut> =>
    request<TimeEntryOut>(`/api/v1/projects/${projectId}/time-entries/`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  hoursSummary: (projectId: string): Promise<HoursSummaryOut> =>
    request<HoursSummaryOut>(`/api/v1/projects/${projectId}/hours-summary/`),
};
